import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';
import PlayerSearchResults from '../playerSearchResults/PlayerSearchResults';
import './playerTagPicker.css';

const DEFAULT_PROFILE_PHOTO =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';

const normalizeSearchText = (value = '') =>
  String(value).trim().replace(/^@+/, '').trim();

const normalizeId = (value) => String(value || '').trim();

export default function PlayerTagPicker({
  selectedPlayers = [],
  onChange,
  token,
  currentPlayerId = '',
  currentPlayerMongoId = '',
  disabled = false,
  maxTags = 5,
  label = 'Tag players',
  initialQuery = '',
  autoFocus = false,
  onPlayerSelected,
}) {
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);
  const inputIdRef = useRef(
    `player-tag-search-${Math.random().toString(36).slice(2)}`,
  );
  const searchRequestRef = useRef(0);
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedIds = useMemo(
    () => new Set(selectedPlayers.map((player) => normalizeId(player._id))),
    [selectedPlayers],
  );
  const selectedKey = useMemo(
    () => selectedPlayers.map((player) => normalizeId(player._id)).join('|'),
    [selectedPlayers],
  );

  const searchText = normalizeSearchText(query);
  const atLimit = selectedPlayers.length >= maxTags;

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setOpen(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    const nextQuery = String(initialQuery || '');
    if (!nextQuery) return;
    setQuery(nextQuery);
    setOpen(true);
  }, [initialQuery]);

  useEffect(() => {
    if (!autoFocus) return;
    searchInputRef.current?.focus();
  }, [autoFocus, initialQuery]);

  const selectPlayer = useCallback(
    (player) => {
      const playerMongoId = normalizeId(player?._id);
      const playerId = normalizeId(player?.playerId);
      if (!playerMongoId || !playerId) return;
      if (playerMongoId === normalizeId(currentPlayerMongoId)) return;
      if (playerId === normalizeId(currentPlayerId)) return;
      if (selectedIds.has(playerMongoId)) {
        clearSearch();
        return;
      }
      if (selectedPlayers.length >= maxTags) {
        clearSearch();
        return;
      }

      onChange?.([
        ...selectedPlayers,
        {
          _id: playerMongoId,
          playerId,
          profilePhoto: player.profilePhoto || DEFAULT_PROFILE_PHOTO,
        },
      ]);
      onPlayerSelected?.({
        _id: playerMongoId,
        playerId,
        profilePhoto: player.profilePhoto || DEFAULT_PROFILE_PHOTO,
      });
      clearSearch();
    },
    [
      clearSearch,
      currentPlayerId,
      currentPlayerMongoId,
      maxTags,
      onChange,
      onPlayerSelected,
      selectedIds,
      selectedPlayers,
    ],
  );

  const removePlayer = useCallback(
    (playerMongoId) => {
      const removeId = normalizeId(playerMongoId);
      onChange?.(
        selectedPlayers.filter((player) => normalizeId(player._id) !== removeId),
      );
    },
    [onChange, selectedPlayers],
  );

  useEffect(() => {
    if (disabled || !searchText || atLimit) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return undefined;
    }

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axiosConn.get(
          `players/regex/${encodeURIComponent(searchText)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (searchRequestRef.current !== requestId) return;
        const players = res?.data?.data?.data || [];
        setResults(
          players.filter((player) => {
            const id = normalizeId(player?._id);
            const playerId = normalizeId(player?.playerId);
            return (
              id &&
              playerId &&
              id !== normalizeId(currentPlayerMongoId) &&
              playerId !== normalizeId(currentPlayerId) &&
              !selectedIds.has(id)
            );
          }),
        );
        setOpen(true);
      } catch {
        if (searchRequestRef.current !== requestId) return;
        setResults([]);
        setOpen(true);
      } finally {
        if (searchRequestRef.current === requestId) setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [
    atLimit,
    currentPlayerId,
    currentPlayerMongoId,
    disabled,
    searchText,
    selectedIds,
    selectedKey,
    token,
  ]);

  useEffect(() => {
    if (!open && !query) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      clearSearch();
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [clearSearch, open, query]);

  return (
    <div className="playerTagPicker" ref={rootRef}>
      <label className="playerTagPickerLabel" htmlFor={inputIdRef.current}>
        {label}
      </label>
      <div className="playerTagPickerShell">
        <span className="playerTagPickerAt" aria-hidden="true">
          @
        </span>
        <input
          ref={searchInputRef}
          id={inputIdRef.current}
          className="playerTagPickerInput"
          type="text"
          value={query}
          disabled={disabled || atLimit}
          placeholder={atLimit ? 'Tag limit reached' : 'Type @playerId'}
          aria-label={label}
          onChange={(event) => {
            setQuery(event.target.value);
            if (event.target.value.trim()) setOpen(true);
          }}
          onFocus={() => {
            if (searchText) setOpen(true);
          }}
        />
        {loading ? <span className="playerTagPickerStatus">Searching</span> : null}
      </div>

      {selectedPlayers.length ? (
        <div className="playerTagChips" aria-label="Tagged players">
          {selectedPlayers.map((taggedPlayer) => (
            <span className="playerTagChip" key={taggedPlayer._id}>
              <img
                src={taggedPlayer.profilePhoto || DEFAULT_PROFILE_PHOTO}
                alt=""
                className="playerTagChipImg"
              />
              @{taggedPlayer.playerId}
              <button
                type="button"
                className="playerTagChipRemove"
                onClick={() => removePlayer(taggedPlayer._id)}
                disabled={disabled}
                aria-label={`Remove ${taggedPlayer.playerId}`}
              >
                x
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {open && searchText ? (
        <div className="playerTagResults" data-testid="player-tag-results">
          {results.length ? (
            <PlayerSearchResults
              searchedPlayers={results}
              selectable
              onPlayerSelect={selectPlayer}
            />
          ) : (
            <div className="playerTagNoResults">
              {loading ? 'Searching players...' : 'No players found'}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export { normalizeSearchText };
